import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Clock, 
  Settings, 
  TestTube, 
  DollarSign, 
  Rocket, 
  CheckCircle,
  ArrowRight,
  Sparkles
} from 'lucide-react';

const ChatbotPhaseRoadmap = ({ chatbot, className = "" }) => {
  const phases = [
    {
      id: 'pending',
      name: 'Pending',
      description: 'Chatbot created and waiting for review',
      icon: Clock,
      color: 'bg-gray-100',
      textColor: 'text-gray-600',
      iconColor: 'text-gray-500'
    },
    {
      id: 'in_progress',
      name: 'In Progress',
      description: 'Our team is building your chatbot',
      icon: Settings,
      color: 'bg-blue-100',
      textColor: 'text-blue-700',
      iconColor: 'text-blue-600'
    },
    {
      id: 'testing',
      name: 'Testing',
      description: 'Ready for you to test and provide feedback',
      icon: TestTube,
      color: 'bg-purple-100',
      textColor: 'text-purple-700',
      iconColor: 'text-purple-600'
    },
    {
      id: 'final_payment',
      name: 'Final Payment',
      description: 'Complete setup payment and choose your plan',
      icon: DollarSign,
      color: 'bg-amber-100',
      textColor: 'text-amber-700',
      iconColor: 'text-amber-600'
    },
    {
      id: 'deploy',
      name: 'Deploy',
      description: 'Get your deployment codes and go live',
      icon: Rocket,
      color: 'bg-green-100',
      textColor: 'text-green-700',
      iconColor: 'text-green-600'
    },
    {
      id: 'done',
      name: 'Live',
      description: 'Your chatbot is live and serving customers',
      icon: CheckCircle,
      color: 'bg-emerald-100',
      textColor: 'text-emerald-700',
      iconColor: 'text-emerald-600'
    }
  ];

  const currentPhaseIndex = phases.findIndex(phase => phase.id === chatbot?.status);
  const currentPhase = phases[currentPhaseIndex] || phases[0];

  const getPhaseState = (phaseIndex) => {
    if (phaseIndex < currentPhaseIndex) return 'completed';
    if (phaseIndex === currentPhaseIndex) return 'current';
    return 'upcoming';
  };

  return (
    <Card className={`bg-slate-900/80 backdrop-blur-2xl border border-slate-800/50 shadow-2xl ${className}`}>
      <CardHeader className="border-b border-slate-800/50 pb-6">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center text-xl font-semibold">
            <div className="w-8 h-8 bg-gradient-to-r from-violet-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            Development Progress
          </CardTitle>
          <div className="flex items-center space-x-4">
            <Badge className="bg-gradient-to-r from-violet-500 to-purple-500 text-white border-0 px-4 py-2 shadow-lg">
              {currentPhase.name}
            </Badge>
            <span className="text-gray-400 text-sm">Phase {currentPhaseIndex + 1} of {phases.length}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-8">
        {/* Desktop View - Enhanced Horizontal Layout */}
        <div className="hidden md:block">
          <div className="flex items-center justify-between mb-8">
            {phases.map((phase, index) => {
              const Icon = phase.icon;
              const state = getPhaseState(index);
              
              return (
                <div key={phase.id} className="flex items-center">
                  <div className="flex flex-col items-center relative">
                    {/* Enhanced Phase Icon with Soft Glow on Current */}
                    <div className={`relative w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-all duration-300 ${
                      state === 'completed' 
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 shadow-lg' 
                        : state === 'current' 
                        ? `bg-gradient-to-r ${phase.color.replace('bg-', 'from-')}-500 to-${phase.iconColor.split('-')[1]}-600 shadow-xl animate-soft-glow current-phase-glow` 
                        : 'bg-slate-800/50 border-2 border-slate-700/30'
                    } hover:scale-105`}>
                      <Icon className={`w-7 h-7 transition-all duration-300 ${
                        state === 'completed' 
                          ? 'text-white' 
                          : state === 'current' 
                          ? 'text-white' 
                          : 'text-gray-500'
                      }`} />
                    </div>
                    
                    {/* Enhanced Phase Name */}
                    <h3 className={`font-semibold text-center mb-2 transition-all duration-300 ${
                      state === 'current' 
                        ? 'text-white text-lg' 
                        : state === 'completed' 
                        ? 'text-green-300 text-base' 
                        : 'text-gray-500 text-sm'
                    }`}>
                      {phase.name}
                    </h3>
                    
                    {/* Enhanced Phase Description */}
                    <p className={`text-xs text-center max-w-[130px] leading-relaxed transition-all duration-300 ${
                      state === 'current' 
                        ? 'text-gray-200' 
                        : 'text-gray-500'
                    }`}>
                      {phase.description}
                    </p>
                    
                    {/* Completed Check */}
                    {state === 'completed' && (
                      <div className="mt-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  
                  {/* Clean Arrow between phases */}
                  {index < phases.length - 1 && (
                    <div className="relative mx-8">
                      <ArrowRight className={`w-8 h-8 transition-all duration-300 ${
                        index < currentPhaseIndex 
                          ? 'text-green-400' 
                          : index === currentPhaseIndex
                          ? 'text-violet-400'
                          : 'text-gray-600'
                      }`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Enhanced Progress Bar */}
          <div className="relative w-full bg-slate-800/50 rounded-full h-3 mb-4 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-violet-500 via-purple-500 to-violet-400 h-3 rounded-full transition-all duration-1000 animate-subtle-shimmer"
              style={{
                width: `${((currentPhaseIndex + 1) / phases.length) * 100}%`
              }}
            />
          </div>
          
          {/* Progress Text */}
          <div className="text-center">
            <span className="text-lg font-semibold text-white">
              {Math.round(((currentPhaseIndex + 1) / phases.length) * 100)}% Complete
            </span>
          </div>
        </div>

        {/* Mobile View - Vertical Layout */}
        <div className="md:hidden space-y-4">
          {phases.map((phase, index) => {
            const Icon = phase.icon;
            const state = getPhaseState(index);
            
            return (
              <div key={phase.id} className="flex items-center space-x-4 p-3 rounded-lg border border-gray-200">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  state === 'completed' 
                    ? 'bg-green-100' 
                    : state === 'current' 
                    ? phase.color 
                    : 'bg-gray-50'
                }`}>
                  <Icon className={`w-5 h-5 ${
                    state === 'completed' 
                      ? 'text-green-600' 
                      : state === 'current' 
                      ? phase.iconColor 
                      : 'text-gray-400'
                  }`} />
                </div>
                <div className="flex-1">
                  <h3 className={`font-medium ${
                    state === 'current' 
                      ? 'text-gray-900' 
                      : state === 'completed' 
                      ? 'text-green-700' 
                      : 'text-gray-500'
                  }`}>
                    {phase.name}
                  </h3>
                  <p className={`text-sm ${
                    state === 'current' 
                      ? 'text-gray-600' 
                      : 'text-gray-500'
                  }`}>
                    {phase.description}
                  </p>
                </div>
                {state === 'completed' && (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                )}
                {state === 'current' && (
                  <div className="w-2 h-2 bg-violet-600 rounded-full"></div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Current Phase Summary */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentPhase.color}`}>
              <currentPhase.icon className={`w-4 h-4 ${currentPhase.iconColor}`} />
            </div>
            <div>
              <h4 className="text-gray-900 font-medium">What's happening now?</h4>
              <p className="text-gray-600 text-sm">{currentPhase.description}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChatbotPhaseRoadmap;